/* --- Swym Custom Wishlist (FULL with auto-refreshing counter) --- */

/* Helper: unique wishlist item count (dedup by epi) */
const _uniqueWishlistCount = (lists = []) => {
  const map = new Map();
  for (const list of lists || []) {
    for (const item of list.listcontents || []) {
      if (!map.has(item.epi)) map.set(item.epi, true);
    }
  }
  return map.size;
};

/* Ensure list exists and is ready */
const ensureListReady = async (swat) => {
  const lists = await fetchList(swat);
  if (!lists?.length || !window.swymSelectedListId) {
    const lid = await createList(swat);
    window.swymSelectedListId = lid;
    await refreshList(swat);
  }
};

/* Initialize wishlist system */
const init = async (swat) => {
  await ensureListReady(swat);
  addSwymEventListener(swat);
  updateButtonState();

  // Emit once on init so counters mounting late still sync immediately
  if (Array.isArray(window.swymWishLists)) {
    window.dispatchEvent(
      new CustomEvent("swym:lists:updated", {
        detail: {
          lists: window.swymWishLists,
          count: _uniqueWishlistCount(window.swymWishLists),
        },
      })
    );
  }
};

/* Create a new wishlist */
const createList = (swat) =>
  new Promise((resolve, reject) => {
    const listConfig = { lname: "My Wishlist" };
    swat.createList(
      listConfig,
      ({ lid }) => {
        console.log("Created List", lid);
        resolve(lid);
      },
      (error) => {
        console.log("Error creating List", error);
        reject(error);
      }
    );
  });

/* Fetch wishlist(s) */
const fetchList = (swat) =>
  new Promise((resolve, reject) => {
    const onSuccess = (lists = []) => {
      console.log("Fetched Lists", lists);
      window.swymWishLists = lists;
      window.swymSelectedListId =
        lists?.[0]?.lid || window.swymSelectedListId || null;
      resolve(lists);
    };
    const onError = (error) => {
      console.log("Error fetching Lists", error);
      reject(error);
    };
    if (!window.swymWishLists) {
      // Support both keys just in case Swym SDK variant differs
      swat.fetchLists({ callbackFn: onSuccess, onError, errorFn: onError });
    } else {
      resolve(window.swymWishLists);
    }
  });

/* Refresh wishlist (now emits global event for counters) */
const refreshList = async (swat) => {
  window.swymWishLists = null;
  const lists = await fetchList(swat);

  // Tell all counters / any listeners that the lists changed
  window.dispatchEvent(
    new CustomEvent("swym:lists:updated", {
      detail: { lists, count: _uniqueWishlistCount(lists) },
    })
  );

  requestAnimationFrame(updateButtonState);
};

/* Add product */
const addToWishlist = async (swat, product) => {
  if (!window.swymSelectedListId) await ensureListReady(swat);
  const lid = window.swymSelectedListId;
  swat.addToList(
    lid,
    product,
    async (added) => {
      console.log("Added", added);
      await refreshList(swat);
    },
    () =>
      swat.ui.showErrorNotification?.({
        message: "Error Adding Product to Wishlist",
      })
  );
};

/* Remove product */
const removeFromWishlist = async (swat, product) => {
  if (!window.swymSelectedListId) await ensureListReady(swat);
  const lid = window.swymSelectedListId;
  swat.deleteFromList(
    lid,
    product,
    async (deleted) => {
      console.log("Removed", deleted);
      await refreshList(swat);
    },
    () =>
      swat.ui.showErrorNotification?.({
        message: "Error removing Product from Wishlist",
      })
  );
};

/* Update button state */
const updateButtonState = () => {
  const buttons = document.querySelectorAll("swymcs-wishlist-button");
  if (!buttons.length || !window.swymWishLists?.length) return;

  buttons.forEach((el) => {
    const { wishlistButton } = el.elements || {};
    if (!wishlistButton) return;
    const { epi, empi } = wishlistButton.dataset;
    let isWishlisted = false;

    for (const list of window.swymWishLists) {
      const items = list?.listcontents || [];
      if (
        items.some(
          (i) => String(i.empi) === String(empi) && String(i.epi) === String(epi)
        )
      ) {
        isWishlisted = true;
        break;
      }
    }
    updateButtonWishlistState(el.elements, isWishlisted);
  });
};

/* Toggle button UI */
const updateButtonWishlistState = (elements, isWishlisted) => {
  if (!elements || !elements.wishlistButton) return;

  elements.wishlistButton.classList.toggle(
    "swym-added-custom",
    !!isWishlisted
  );
  elements.wishlistButton.setAttribute(
    "data-wishlisted",
    isWishlisted ? "true" : "false"
  );

  if (elements.filledState)
    elements.filledState.style.display = isWishlisted ? "flex" : "none";
  if (elements.unfilledState)
    elements.unfilledState.style.display = isWishlisted ? "none" : "flex";

  const settingsRaw = elements.wishlistButton.dataset.settings || "{}";
  let settings = {};
  try {
    settings = JSON.parse(settingsRaw);
  } catch (e) {}
  if (isWishlisted && settings.disable_added_to_wishlist) {
    elements.wishlistButton.setAttribute("disabled", "true");
  } else {
    elements.wishlistButton.removeAttribute("disabled");
  }
};

/* Render popup */
const renderPopup = async (product) => {
  const popup = document.querySelector("swym-wishlist-popup");
  const { epi, du } = product;
  window.currentActiveProduct = await fetchProductsData(du);
  if (!window.currentActiveProduct) return;
  window.currentActiveVariant = window.currentActiveProduct.variants.find(
    (v) => String(v.id) === String(epi)
  );
  window.currentActiveVariantId = epi;
  window.currentActiveProduct.url = du;
  setTimeout(() => popup && popup._show && popup._show(), 100);
};

/* Fetch product JSON */
const fetchProductsData = async (url) => {
  const res = await fetch(`${url}.js`);
  if (!res.ok) return null;
  return res.json();
};

/* Event listeners */
const addSwymEventListener = (swat) => {
  const resync = () => refreshList(swat).then(updateButtonState);

  const escapeHTML = (s = "") =>
    String(s).replace(/[&<>"']/g, (m) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[m]));

  const notify = ({ image, title, action }) => {
    const safeTitle = escapeHTML(title || "Product");
    const imgHTML = image
      ? `<div class="notification__img img-ar img-ar--cover" style="--aspect-ratio:1/1;">
           <img src="${escapeHTML(image)}" alt="${safeTitle}" class="w-full h-full object-cover">
         </div>`
      : "";
    const text =
      action === "remove"
        ? `<strong class="title">"${safeTitle}"</strong> removed from wishlist`
        : `<strong class="title">"${safeTitle}"</strong> added to wishlist`;

    if (window.NotificationCenter?.notify) {
      NotificationCenter.notify(
        `${imgHTML}<p class="notification__text">${text}</p>`,
        {
          type: action === "remove" ? "wishlist-remove" : "wishlist-add",
          duration: 4000,
        }
      );
    } else if (swat?.ui?.showSuccessNotification) {
      swat.ui.showSuccessNotification({ message: text });
    } else {
      console.log("[Wishlist]", text.replace(/<[^>]*>/g, ""));
    }
  };

  swat.evtLayer.addEventListener(
    swat.JSEvents.removedFromWishlist,
    (event) => {
      const { iu: image, dt: title } = event.detail?.d || {};
      notify({ image, title, action: "remove" });
      resync();
    }
  );

  swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
    const { iu: image, dt: title } = event.detail?.d || {};
    notify({ image, title, action: "add" });
    resync();
  });

  swat.evtLayer.addEventListener(swat.JSEvents.variantChanged, (data) => {
    const currentVariant = data.detail?.d?.variant?.id;
    const currentProductId = data.detail?.d?.product?.id;
    if (currentVariant && currentProductId) {
      const btn = document.querySelector(
        `.swymcs-wishlist-button-${currentProductId} button`
      );
      if (btn) btn.setAttribute("data-epi", currentVariant);
      setTimeout(updateButtonState, 200);
    }
  });
};

/* Global export */
const swymcsWishlistFunctions = {
  init,
  fetchList,
  refreshList,
  updateButtonState,
  updateButtonWishlistState,
  addToWishlist,
  removeFromWishlist,
  addSwymEventListener,
  fetchProductsData,
  renderPopup,
};

if (!window.SwymCallbacks) window.SwymCallbacks = [];
window.SwymCallbacks.push(swymcsWishlistFunctions.init);
window.swymcsWishlistFunctions = swymcsWishlistFunctions;

/* Custom button element */

if (!customElements.get("swymcs-wishlist-button")) {
  class SwymWishlistButton extends HTMLElement {
    constructor() {
      super();
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(this.initializeSwymButton.bind(this));
    }

    async initializeSwymButton(swat) {
      this.elements = {
        wishlistButton: this.querySelector("button"),
        unfilledState: this.querySelector(".state--unfilled") || null,
        filledState: this.querySelector(".state--filled") || null,
      };

      if (!this.elements.wishlistButton) return;

      this.elements.wishlistButton.addEventListener("click", () => {
        let { epi, empi, du } = this.elements.wishlistButton.dataset;
        let product = { epi, empi, du };

        if (swat.isCollectionsEnabled()) {
          swymcsWishlistFunctions.renderPopup(product);
        } else {
          if (
            this.elements.wishlistButton.classList.contains("swym-added-custom")
          ) {
            swymcsWishlistFunctions.removeFromWishlist(swat, product);
          } else {
            swymcsWishlistFunctions.addToWishlist(swat, product);
          }
        }
      });
      this.classList.add("loaded");
    }
  }

  customElements.define("swymcs-wishlist-button", SwymWishlistButton);
}

/* Counter element (auto-refreshes via swym:lists:updated) */
if (!customElements.get("swym-wishlist-counter")) {
  class SwymWishlistCounter extends HTMLElement {
    constructor() {
      super();
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(this._initializeCounter.bind(this));
    }

    connectedCallback() {
      this._onListsUpdated = (e) => {
        this.textContent = e?.detail?.count ?? "0";
      };
      window.addEventListener("swym:lists:updated", this._onListsUpdated);

      if (Array.isArray(window.swymWishLists)) {
        this.textContent = _uniqueWishlistCount(window.swymWishLists);
      }
    }

    disconnectedCallback() {
      window.removeEventListener("swym:lists:updated", this._onListsUpdated);
    }

    async _initializeCounter(swat) {
      try {
        const listData = Array.isArray(window.swymWishLists)
          ? window.swymWishLists
          : await new Promise((resolve, reject) => {
              swat.fetchLists({
                callbackFn: (data) => resolve(data),
                onError: (error) => reject(error),
                errorFn: (error) => reject(error),
              });
            });

        this.textContent = _uniqueWishlistCount(listData);

        // Emit once to normalize state for any other counters already mounted
        window.dispatchEvent(
          new CustomEvent("swym:lists:updated", {
            detail: { lists: listData, count: _uniqueWishlistCount(listData) },
          })
        );
      } catch (err) {
        console.error("Error fetching wishlist data:", err);
      }
    }
  }
  customElements.define("swym-wishlist-counter", SwymWishlistCounter);
}
